import React, { useCallback, useEffect, useRef } from 'react'

import { systemFieldsSelector } from 'features/assets/model/selectors/assetsSelectors'
import { IField } from 'features/assets/model/types/assets'
import {
  selectSearchAiTagsSelector,
  selectSearchInputValueSelector,
  selectSearchIsSearchInputActiveSelector,
  selectSearchLabelsSelector,
  selectSearchPrevStateSearchSelector,
  selectSearchResultsAiTagsSelector,
  selectSearchResultsSelector,
  selectSearchStatusSelector,
  selectSearchTagsSelector
} from 'features/common/model/selectors/searchSelectors'
import {
  fetchLabels,
  fetchSearchResults,
  revertPrevStateSearch,
  setIsSearchInputActive,
  setIsTagMenuVisible,
  setSearchInputValue
} from 'features/common/model/slice/searchSlice'
import { SearchNode } from 'shared/api/searchApi'
import { useAppDispatch, useAppSelector } from 'shared/config/store'
import { useOnClickOutside } from 'shared/hooks/useOnclickOutside'
import styled from 'styled-components'
import { useNavigate } from 'react-router-dom'

import { ROUTES } from '../../../../shared/constants/routesPaths'

import SearchInputField from './SearchInputField'
import { SearchMenu } from './SearchMenu'

export const SearchInput: React.FC = () => {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()

  const systemFields = useAppSelector(systemFieldsSelector)
  const tags = systemFields.find((field: IField) => field.title === 'tags')
  const statuses = systemFields.find((field: IField) => field.title === 'status')
  const labels = useAppSelector(selectSearchLabelsSelector)
  const inputValue = useAppSelector(selectSearchInputValueSelector)
  const isSearchInputActive = useAppSelector(selectSearchIsSearchInputActiveSelector)
  const searchTags = useAppSelector(selectSearchTagsSelector)
  const searchAiTags = useAppSelector(selectSearchAiTagsSelector)
  const searchStatus = useAppSelector(selectSearchStatusSelector)
  const resultsSearchAiTags = useAppSelector(selectSearchResultsAiTagsSelector)
  const searchWrapperRef = useRef<any>(null)
  const selectTagsRef = useRef<any>(null)
  const searchResults = useAppSelector(selectSearchResultsSelector)
  const prevStateSearch = useAppSelector(selectSearchPrevStateSearchSelector)

  const tagMenuRef = useRef<any>(null)

  useOnClickOutside(searchWrapperRef, () => {
    if(prevStateSearch.searchAiTags.length > 0 || prevStateSearch.searchStatus.length > 0) {
      dispatch(revertPrevStateSearch())
      dispatch(setIsSearchInputActive(false))
    } else{
      dispatch(setIsSearchInputActive(false))
      dispatch(setIsTagMenuVisible(false))
    }
  }, [tagMenuRef])

  const handleChange = useCallback((value: string) => {
    dispatch(setSearchInputValue(value))
  }, [dispatch])

  const handleSearch = useCallback((value: string) => {
    dispatch(fetchSearchResults({ value }))
  }, [dispatch])

  const handleClear = useCallback(() => {
    navigate(ROUTES.dashboard.allAssets)
  }, [dispatch])

  useEffect(() => {
    dispatch(fetchLabels())
  }, [dispatch])


  return (
    <SSearchWrapper
      ref={searchWrapperRef}
      $isActive={isSearchInputActive || searchTags.length > 0 || searchAiTags.length > 0 || searchStatus.length > 0}
      onClick={() => {
        dispatch(setIsSearchInputActive(true))
      }}
    >
      <SearchInputField
        isActive={isSearchInputActive || searchTags.length > 0 || searchAiTags.length > 0 || searchStatus.length > 0}
        inputValue={inputValue}
        onSearch={handleSearch}
        onClear={handleClear}
      />

      {isSearchInputActive && (
        <SearchMenu
          searchAiTags={resultsSearchAiTags}
          tagMenuRef={tagMenuRef}
          isTagsSearchMenuVisible={isSearchInputActive}
          inputValue={inputValue}
          labels={labels}
          statuses={statuses?.options}
          tags={tags?.options}
          searchAssets={searchResults?._embedded?.nodes?.filter((node: SearchNode) => node.type === 'file') || []}
          searchBoards={searchResults?._embedded?.nodes?.filter((node: SearchNode) => node.type === 'dir') || []}
          selectTagsRef={selectTagsRef}
        />
      )}
    </SSearchWrapper>
  )
}

const SSearchWrapper = styled.div<{ $isActive: boolean }>`
  min-width: ${props => props.$isActive ? '528px' : '255px'};
  transition: min-width 0.3s ease;
  position: absolute;
  right: 225px;
  z-index: 5;
  
  .ant-input-affix-wrapper {
    width: 100%;
    background: #FFF;
    border-radius: 6px;
    box-shadow: -5px 0 10px rgba(255, 255, 255, 1);
  }
  
  &:before {
    content: '';
    position: absolute;
    top: -10px;
    left: -40px;
    height: calc(100% + 20px);
    width: 50px;
    background: linear-gradient(90deg, 
      rgba(255, 255, 255, 0) 0%, 
      rgba(255, 255, 255, 0.85) 60%, 
      rgba(255, 255, 255, 0.95) 80%,
      rgba(255, 255, 255, 1) 100%
    );
    pointer-events: none;
    z-index: 3;
  }
`
